import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import { layout } from "@/constants";
import Modal from "./base/Modal";
import NotificationCard from "./base/NotificationCard";
import Text from "./base/Text";
import TextButton from "./base/TextButton";
import TextInput from "./base/TextInput";
import Pill from "./base/Pill";
import ScrollView from "./base/ScrollView";

interface FeedbackType {
  label: string;
  description: string;
}

const feedbackTypes: FeedbackType[] = [
  {
    label: "Technical Issue",
    description:
      "Anything that breaks or doesn’t work: crashes, “voice output cut out,” “doesn’t listen,” network/connection errors, login/auth problems."
  },
  {
    label: "Unhelpful Response",
    description:
      "Stupid, superficial, off-topic or “didn’t get me” replies; tone or content feels wrong or unsupportive."
  },
  {
    label: "Confusing Experience",
    description:
      "UI or flow issues: slow loading, confusing navigation, unclear prompts, “too many steps,” layout glitches."
  },
  {
    label: "Feature Request",
    description:
      "Suggestions for new capabilities or improvements—“would be great if…” items."
  },
  {
    label: "Other Feedback",
    description:
      "Anything else that doesn’t fit above—general praise, privacy concerns, etc."
  }
];

/**
 * Props for the FeedbackModal component
 */
interface FeedbackModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Whether the feedback is anchored to a reported conversation turn. When
   *  anchored, the flagged turn itself carries the signal, so the details text
   *  is optional; otherwise details are required. */
  isAnchored: boolean;
  /** Callback when modal should be closed */
  onClose: () => void;
  /** Callback when feedback is submitted. May reject; the modal renders
   *  the rejection's message inline via NotificationCard. */
  onSubmit: (feedback: {
    text: string;
    feedbackTypes: string[];
  }) => Promise<void>;
}

/**
 * Modal component for collecting user feedback.
 *
 * When opened from a reported conversation turn, the caller passes that turn's
 * id along to the server; the reported message and its surrounding context are
 * resolved server-side and never shown here.
 */
export default function FeedbackModal({
  visible,
  isAnchored,
  onClose,
  onSubmit
}: FeedbackModalProps) {
  const [text, setText] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize a fresh form only on the closed→open transition.
  const wasVisible = useRef(false);
  useEffect(() => {
    if (visible && !wasVisible.current) {
      setText("");
      setError(null);
      setSelectedTypes([]);
    }
    wasVisible.current = visible;
  }, [visible]);

  const handleTypeToggle = (typeId: string) => {
    setSelectedTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleTextChange = (next: string) => {
    setText(next);
    if (error) setError(null);
  };

  // Anchored feedback can be submitted with empty details (the flagged turn is
  // the signal); general feedback still needs something written.
  const canSubmit = !isSubmitting && (isAnchored || !!text.trim());

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({
        text: text.trim(),
        feedbackTypes: selectedTypes
      });
      onClose();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to send feedback. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      size="wide"
      title="Send Feedback"
      titleAlign="center"
      onRequestClose={onClose}
      footer={
        <>
          <TextButton
            buttonText="Cancel"
            variant="secondary"
            onPress={onClose}
            accessibilityLabel="Cancel feedback"
          />
          <TextButton
            buttonText={isSubmitting ? "Sending..." : "Submit"}
            disabled={!canSubmit}
            onPress={handleSubmit}
            accessibilityLabel="Submit feedback"
          />
        </>
      }
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={false}
      >
        <View style={styles.section}>
          <Text>What type of feedback is this? (optional)</Text>
          <View style={styles.typesContainer}>
            {feedbackTypes.map((type) => (
              <Pill
                key={type.label}
                label={type.label}
                isSelected={selectedTypes.includes(type.label)}
                onPress={() => handleTypeToggle(type.label)}
                accessibilityLabel={`${type.label} feedback type`}
              />
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text>
            {isAnchored
              ? "Please provide details: (optional)"
              : "Please provide details:"}
          </Text>
          <View style={styles.textInputContainer}>
            <TextInput
              value={text}
              onChangeText={handleTextChange}
              placeholder="Describe your experience, issue, or suggestion..."
              maxLength={1000}
              multiline
              style={styles.feedbackTextInput}
            />
          </View>
          <Text size="small" color="secondary" style={styles.characterCount}>
            {text.length}/1000 characters
          </Text>
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <NotificationCard variant="error" label="Error" message={error} />
          </View>
        )}
      </ScrollView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: layout.space.xl
  },
  typesContainer: {
    marginTop: layout.space.sm,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: layout.space.sm
  },
  textInputContainer: {
    marginTop: layout.space.md
  },
  feedbackTextInput: {
    borderRadius: layout.borderRadius.medium,
    minHeight: 120,
    maxHeight: undefined
  },
  characterCount: {
    textAlign: "right",
    marginTop: layout.space.sm
  },
  errorContainer: {
    marginTop: layout.space.md
  }
});
